import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { AuthContext } from "../../shared/context/auth-context";
import { UnitPreferencesContext } from "../../shared/context/unit-preferences-context";
import Button from "../../shared/components/FormElements/Button";
import Icon from "../../shared/components/UIElements/Icon";
import SelectDropdown from "../../shared/components/FormElements/SelectDropdown";
import AssistantMarkdown from "./AssistantMarkdown";
import { assistantService, unitsForAssistant } from "../services/assistantService";
import { useReportedAssistantView } from "../context/AssistantViewContext";
import { pageNameFor } from "../utils/pageNames";
import "./AskCadenza.css";

/*
  AskCadenza
  Purpose: the in-app AI assistant — a launcher button and a right-hand
  drawer that answers questions about the selected project by streaming
  Maestro's /v1/assistant/chat. Read-only: the assistant can look at
  scenarios, results, heaps, schedules and site data, never change them.

  Renders nothing unless the project's company has turned Ask Cadenza on and
  added a key (GET /v1/assistant/status), so companies that haven't opted in
  never see AI UI.

  Props:
    - visible: boolean — whether the current route should offer the assistant
*/

const TOOL_LABELS = {
  list_projects: "Listed projects",
  list_scenarios: "Listed scenarios",
  get_scenario_config: "Read scenario setup",
  compare_scenario_configs: "Compared scenario setups",
  list_simulations: "Listed simulation runs",
  get_simulation_status: "Checked run status",
  get_simulation_summary: "Read run summary",
  get_simulation_logs: "Read run warnings",
  query_results: "Read simulation results",
  compare_simulations: "Compared runs",
  query_snapshot: "Read heap snapshot",
  get_simulation_economics: "Read run economics",
  get_econ_config: "Read economics setup",
  query_heap: "Read block model",
  get_heap_layout: "Read heap layout",
  get_block_model: "Read block model",
  get_mine_schedule: "Read stacking schedule",
  get_leach_schedule: "Read leach schedule",
  list_ensembles: "Listed ensembles",
  get_ensemble: "Read ensemble",
  list_closure_jobs: "Listed closure runs",
  get_closure_job: "Read closure run",
  query_closure_results: "Read closure results",
  list_lab_tests: "Read lab test library",
  get_kinetics_fit: "Read kinetics fit",
  list_geode_models: "Listed custom models",
  get_geode_model: "Read custom model",
  list_operational_logs: "Listed operational logs",
  query_operational_data: "Read operational data",
  list_maintenance_assets: "Listed maintenance assets",
  list_maintenance_logs: "Read maintenance logs",
  list_tasks: "Read task board",
  read_guide: "Read a reference guide",
};

const SUGGESTIONS = [
  "Summarise the latest run of this scenario",
  "How does recovery compare with the baseline scenario?",
  "Which panels are scheduled to leach next month?",
];

const MAX_CONTINUES = 6;

const storageKey = (projectId) => `askCadenza:conversation:${projectId}`;
const readStored = (projectId) => {
  try {
    return sessionStorage.getItem(storageKey(projectId));
  } catch {
    return null;
  }
};
const writeStored = (projectId, id) => {
  try {
    if (id) sessionStorage.setItem(storageKey(projectId), id);
    else sessionStorage.removeItem(storageKey(projectId));
  } catch {
    /* storage unavailable — the conversation just won't survive a reload */
  }
};

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const MODEL_KEY = "askCadenza:model";
const readModel = () => {
  try {
    return localStorage.getItem(MODEL_KEY);
  } catch {
    return null;
  }
};
const writeModel = (id) => {
  try {
    localStorage.setItem(MODEL_KEY, id);
  } catch {
    /* storage unavailable — the choice lasts until reload */
  }
};

const AskCadenza = ({ visible }) => {
  const auth = useContext(AuthContext);
  const { unitPreferences } = useContext(UnitPreferencesContext);
  const projectId = auth.selectedProject?._id || auth.selectedProject?.id || null;
  const scenarioId = auth.selectedScenario?._id || auth.selectedScenario?.id || null;
  const location = useLocation();
  const reportedView = useReportedAssistantView();
  const pageName = reportedView?.page || pageNameFor(location.pathname);

  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [items, setItems] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [pickedModel, setPickedModel] = useState(readModel);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const abortRef = useRef(null);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const loadedForRef = useRef(null);

  // The company's model is both the ceiling and the default; a remembered
  // pick only counts while the company still offers it.
  const modelOptions = status?.models || [];
  const selectedModel = modelOptions.some((m) => m.id === pickedModel)
    ? pickedModel
    : status?.model;

  const refreshStatus = useCallback(async () => {
    if (!projectId || !auth.token) return;
    try {
      setStatus(await assistantService.getStatus(projectId, auth.token));
    } catch {
      setStatus({ available: false });
    }
  }, [projectId, auth.token]);

  // Availability is per project (the project's company pays and decides).
  useEffect(() => {
    setStatus(null);
    setItems([]);
    setConversationId(null);
    loadedForRef.current = null;
    if (visible && projectId) refreshStatus();
  }, [visible, projectId, refreshStatus]);

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [items]);

  const updateLast = useCallback((fn) => {
    setItems((prev) => {
      const next = [...prev];
      const last = next[next.length - 1];
      if (!last || last.role !== "assistant") return prev;
      next[next.length - 1] = fn({ ...last, tools: [...last.tools] });
      return next;
    });
  }, []);

  const runStream = useCallback(
    async (start) => {
      const controller = new AbortController();
      abortRef.current = controller;
      setBusy(true);
      let pendingContinue = false;
      let convId = null;
      const onEvent = (event, data) => {
        switch (event) {
          case "conversation":
            convId = data.id;
            setConversationId(data.id);
            writeStored(projectId, data.id);
            break;
          case "text":
            updateLast((m) => ({ ...m, text: m.text + data.delta }));
            break;
          case "tool_start":
            updateLast((m) => ({
              ...m,
              tools: [...m.tools, { id: data.id, name: data.name, pending: true, ok: true }],
            }));
            break;
          case "tool_done":
            updateLast((m) => ({
              ...m,
              tools: m.tools.map((t) =>
                t.id === data.id ? { ...t, pending: false, ok: data.ok } : t
              ),
            }));
            break;
          case "refusal":
          case "error":
            updateLast((m) => ({ ...m, error: data.message }));
            break;
          case "truncated":
            updateLast((m) => ({
              ...m,
              notice: "The answer was cut off at its length limit.",
            }));
            break;
          case "continue":
            pendingContinue = true;
            break;
          default:
            break;
        }
      };
      try {
        await start(onEvent, controller.signal);
        for (let n = 0; pendingContinue && n < MAX_CONTINUES && convId; n++) {
          pendingContinue = false;
          await assistantService.continue(convId, projectId, auth.token, onEvent, controller.signal);
        }
      } catch (err) {
        if (err.name !== "AbortError") {
          updateLast((m) => ({
            ...m,
            error: err.message || "Ask Cadenza couldn't answer. Try again.",
          }));
        }
      } finally {
        updateLast((m) => ({
          ...m,
          streaming: false,
          tools: m.tools.map((t) => ({ ...t, pending: false })),
        }));
        setBusy(false);
        abortRef.current = null;
        refreshStatus();
      }
    },
    [projectId, auth.token, updateLast, refreshStatus]
  );

  const send = useCallback(
    (text) => {
      const message = (text ?? input).trim();
      if (!message || busy || !projectId) return;
      setInput("");
      setItems((prev) => [
        ...prev,
        { role: "user", text: message },
        { role: "assistant", text: "", tools: [], streaming: true },
      ]);
      runStream((onEvent, signal) =>
        assistantService.ask(
          {
            projectId,
            scenarioId: conversationId ? undefined : scenarioId,
            conversationId,
            message,
            model: selectedModel,
            units: conversationId ? undefined : unitsForAssistant(unitPreferences),
            // What is on screen as this question is asked; Maestro keeps only
            // records from this project and tells the model it is context.
            view: {
              page: pageName || undefined,
              scenarioId: scenarioId || undefined,
              simulationId: reportedView?.simulationId,
              heapId: reportedView?.heapId,
              date: reportedView?.date,
            },
          },
          auth.token,
          onEvent,
          signal
        )
      );
    },
    [input, busy, projectId, scenarioId, conversationId, unitPreferences, pageName, reportedView, selectedModel, auth.token, runStream]
  );

  const loadConversation = useCallback(
    async (id) => {
      try {
        const data = await assistantService.getConversation(id, projectId, auth.token);
        setConversationId(id);
        writeStored(projectId, id);
        setItems(
          data.items.map((it) =>
            it.role === "assistant" ? { ...it, tools: it.tools || [] } : it
          )
        );
        if (data.conversation.status === "continuing") {
          setItems((prev) => [...prev, { role: "assistant", text: "", tools: [], streaming: true }]);
          runStream((onEvent, signal) =>
            assistantService.continue(id, projectId, auth.token, onEvent, signal)
          );
        }
      } catch {
        writeStored(projectId, null);
        setConversationId(null);
        setItems([]);
      }
    },
    [projectId, auth.token, runStream]
  );

  // Reopen the last conversation for this project (per browser tab).
  useEffect(() => {
    if (!open || !status?.available || loadedForRef.current === projectId) return;
    loadedForRef.current = projectId;
    const stored = readStored(projectId);
    if (stored && items.length === 0) loadConversation(stored);
  }, [open, status, projectId, items.length, loadConversation]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const newConversation = () => {
    if (busy) return;
    setConversationId(null);
    setItems([]);
    writeStored(projectId, null);
    setHistoryOpen(false);
    inputRef.current?.focus();
  };

  const toggleHistory = async () => {
    if (historyOpen) {
      setHistoryOpen(false);
      return;
    }
    setHistoryOpen(true);
    try {
      const data = await assistantService.listConversations(projectId, auth.token);
      setHistory(data.conversations || []);
    } catch {
      setHistory([]);
    }
  };

  const removeConversation = async (id) => {
    try {
      await assistantService.deleteConversation(id, projectId, auth.token);
      setHistory((h) => h.filter((c) => c.id !== id));
      if (id === conversationId) newConversation();
    } catch {
      /* leave it listed; the next refresh shows the truth */
    }
  };

  if (!visible || !projectId || !status?.available) return null;

  const scenarioName = auth.selectedScenario?.name;

  return (
    <>
      {!open && (
        <button
          type="button"
          className="ask-cadenza__launcher"
          onClick={() => setOpen(true)}
          aria-label="Open Ask Cadenza"
        >
          <Icon name="comment" size={18} />
          <span>Ask Cadenza</span>
        </button>
      )}

      {open && (
        <aside className="ask-cadenza" aria-label="Ask Cadenza">
          <header className="ask-cadenza__head">
            <div className="ask-cadenza__title">
              <strong>Ask Cadenza</strong>
              {modelOptions.length > 1 ? (
                <div className="ask-cadenza__model">
                  <SelectDropdown
                    size="small"
                    items={modelOptions.map((m) => ({ id: m.id, name: m.label }))}
                    selectedId={selectedModel}
                    onChange={(item) => {
                      setPickedModel(item.id);
                      writeModel(item.id);
                    }}
                    disabled={busy}
                  />
                </div>
              ) : null}
              <span>
                {modelOptions.length > 1 ? "" : status.modelLabel}
                {status.companyName
                  ? `${modelOptions.length > 1 ? "" : " · "}via ${status.companyName}`
                  : ""}
              </span>
            </div>
            <div className="ask-cadenza__head-actions">
              <button type="button" onClick={toggleHistory} aria-expanded={historyOpen}>
                History
              </button>
              <button type="button" onClick={newConversation} disabled={busy}>
                New
              </button>
              <button
                type="button"
                className="ask-cadenza__close"
                onClick={() => setOpen(false)}
                aria-label="Close Ask Cadenza"
              >
                ×
              </button>
            </div>
          </header>

          <div className="ask-cadenza__context">
            {auth.selectedProject?.name}
            {scenarioName ? ` › ${scenarioName}` : ""}
            {pageName ? ` · ${pageName}` : ""}
          </div>

          {historyOpen && (
            <div className="ask-cadenza__history">
              {history.length === 0 ? (
                <p className="ask-cadenza__muted">No earlier conversations in this project.</p>
              ) : (
                history.map((c) => (
                  <div className="ask-cadenza__history-row" key={c.id}>
                    <button
                      type="button"
                      className="ask-cadenza__history-open"
                      onClick={() => {
                        setHistoryOpen(false);
                        loadConversation(c.id);
                      }}
                      disabled={busy}
                    >
                      <span>{c.title || "Untitled"}</span>
                      <small>{new Date(c.lastActivityAt).toLocaleDateString()}</small>
                    </button>
                    <button
                      type="button"
                      className="ask-cadenza__history-delete"
                      onClick={() => removeConversation(c.id)}
                      aria-label={`Delete conversation ${c.title}`}
                      disabled={busy}
                    >
                      <Icon name="trash" size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          <div className="ask-cadenza__thread" ref={threadRef}>
            {items.length === 0 && (
              <div className="ask-cadenza__empty">
                <p>
                  Ask about this project's scenarios, simulation results, heaps,
                  leach schedules or site data. Answers quote the records they
                  came from.
                </p>
                <div className="ask-cadenza__suggestions">
                  {SUGGESTIONS.map((s) => (
                    <button type="button" key={s} onClick={() => send(s)}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {items.map((m, idx) =>
              m.role === "user" ? (
                <div className="ask-cadenza__user" key={idx}>
                  {m.text}
                </div>
              ) : (
                <div className="ask-cadenza__answer" key={idx}>
                  {m.tools.length > 0 && (
                    <ul className="ask-cadenza__tools">
                      {m.tools.map((t) => (
                        <li
                          key={t.id}
                          className={
                            t.pending ? "is-pending" : t.ok ? "is-ok" : "is-failed"
                          }
                        >
                          {TOOL_LABELS[t.name] || t.name}
                          {!t.pending && !t.ok && " — failed"}
                        </li>
                      ))}
                    </ul>
                  )}
                  {m.text && <AssistantMarkdown text={m.text} />}
                  {m.streaming && !m.text && (
                    <p className="ask-cadenza__muted">Working…</p>
                  )}
                  {m.notice && <p className="ask-cadenza__muted">{m.notice}</p>}
                  {m.error && <p className="ask-cadenza__error">{m.error}</p>}
                </div>
              )
            )}
          </div>

          <form
            className="ask-cadenza__composer"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <textarea
              id="ask-cadenza-input"
              ref={inputRef}
              rows={2}
              value={input}
              placeholder="Ask about this project…"
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              maxLength={4000}
              disabled={busy}
            />
            <div className="ask-cadenza__composer-row">
              <span className="ask-cadenza__muted">
                Read-only · You: {money(status.userDayUsd)} of {money(status.perUserDailyUsd)} today
              </span>
              {busy ? (
                <Button type="button" size="small" inverse onClick={() => abortRef.current?.abort()}>
                  Stop
                </Button>
              ) : (
                <Button type="submit" size="small" disabled={!input.trim()}>
                  Send
                </Button>
              )}
            </div>
          </form>
        </aside>
      )}
    </>
  );
};

export default AskCadenza;
